// src/pages/PrivacyPolicy.js
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Politique de Confidentialité | Gates of Music</title>
        <meta 
          name="description" 
          content="Politique de confidentialité de Gates of Music. Découvrez comment nous collectons, utilisons et protégeons vos données personnelles." 
        />
        <link rel="canonical" href="https://www.gatesofmusic.com/privacy" />
        <meta name="robots" content="index, follow" />
        
        <meta property="og:title" content="Politique de Confidentialité | Gates of Music" />
        <meta property="og:description" content="Comment Gates of Music protège vos données personnelles" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gatesofmusic.com/privacy" />
      </Helmet>

      <div className="privacy-policy-page">
        <Header />
        
        <main className="privacy-content">
          <div className="privacy-container">
            {/* En-tête */}
            <header className="privacy-header">
              <h1>Politique de Confidentialité</h1>
              <p className="last-updated">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </header>

            {/* Introduction */}
            <section className="privacy-section">
              <div className="section-content">
                <p className="intro-text">
                  Chez <strong>Gates of Music</strong>, nous accordons une grande importance à la protection 
                  de vos données personnelles. Cette politique de confidentialité explique comment nous collectons, 
                  utilisons, partageons et protégeons vos informations lorsque vous utilisez notre plateforme.
                </p>
                <p className="intro-text">
                  En utilisant notre site web et notre application mobile, vous acceptez les pratiques décrites 
                  dans cette politique de confidentialité.
                </p>
              </div>
            </section>

            {/* 1. Informations collectées */}
            <section className="privacy-section">
              <h2>1. Informations que nous collectons</h2>
              
              <div className="subsection">
                <h3>1.1 Informations que vous nous fournissez</h3>
                <ul>
                  <li><strong>Informations de compte :</strong> Nom, prénom, adresse e-mail, numéro de téléphone, mot de passe</li>
                  <li><strong>Informations de profil :</strong> Photo de profil, biographie, spécialités, localisation</li>
                  <li><strong>Informations professionnelles :</strong> Type de prestation, tarifs, portfolio, avis clients</li>
                  <li><strong>Informations de réservation :</strong> Dates, lieux, type d'événement, budget</li>
                  <li><strong>Communications :</strong> Messages échangés via la plateforme, commentaires, avis</li>
                </ul>
              </div>

              <div className="subsection">
                <h3>1.2 Informations collectées automatiquement</h3>
                <ul>
                  <li><strong>Données techniques :</strong> Adresse IP, type de navigateur, système d'exploitation, identifiant de l'appareil</li>
                  <li><strong>Données d'utilisation :</strong> Pages consultées, durée des visites, clics, recherches effectuées</li>
                  <li><strong>Cookies et technologies similaires :</strong> Pour améliorer votre expérience utilisateur</li>
                  <li><strong>Données de localisation :</strong> Avec votre consentement, pour vous proposer des prestataires à proximité</li>
                </ul>
              </div>

              <div className="subsection">
                <h3>1.3 Informations provenant de tiers</h3>
                <ul>
                  <li><strong>Réseaux sociaux :</strong> Si vous vous connectez via Facebook, Google, etc.</li>
                  <li><strong>Partenaires commerciaux :</strong> Informations partagées par nos partenaires avec votre consentement</li>
                </ul>
              </div>
            </section>

            {/* 2. Utilisation des données */}
            <section className="privacy-section">
              <h2>2. Comment nous utilisons vos informations</h2>
              
              <div className="subsection">
                <h3>Nous utilisons vos données pour :</h3>
                <ul>
                  <li>✅ <strong>Fournir nos services :</strong> Créer et gérer votre compte, faciliter les réservations</li>
                  <li>✅ <strong>Améliorer la plateforme :</strong> Analyser l'utilisation, développer de nouvelles fonctionnalités</li>
                  <li>✅ <strong>Communication :</strong> Vous envoyer des notifications, confirmations, newsletters (avec votre consentement)</li>
                  <li>✅ <strong>Sécurité :</strong> Prévenir la fraude, détecter les abus, protéger nos utilisateurs</li>
                  <li>✅ <strong>Support client :</strong> Répondre à vos questions et résoudre les problèmes</li>
                  <li>✅ <strong>Personnalisation :</strong> Vous proposer des recommandations adaptées à vos préférences</li>
                  <li>✅ <strong>Marketing :</strong> Avec votre consentement, vous envoyer des offres promotionnelles</li>
                  <li>✅ <strong>Obligations légales :</strong> Respecter nos obligations juridiques et réglementaires</li>
                </ul>
              </div>
            </section>

            {/* 3. Partage des données */}
            <section className="privacy-section">
              <h2>3. Partage de vos informations</h2>
              
              <div className="subsection">
                <p>Nous ne vendons jamais vos données personnelles. Nous pouvons partager vos informations avec :</p>
                
                <div className="sharing-card">
                  <h4>🤝 Autres utilisateurs</h4>
                  <p>Les prestataires peuvent voir vos informations de contact et détails de réservation. Les clients peuvent voir les profils publics des prestataires.</p>
                </div>

                <div className="sharing-card">
                  <h4>💼 Prestataires de services</h4>
                  <p>Hébergement web, processeurs de paiement, outils d'analyse, services de marketing (sous contrat strict de confidentialité)</p>
                </div>

                <div className="sharing-card">
                  <h4>⚖️ Obligations légales</h4>
                  <p>Autorités gouvernementales si la loi l'exige ou pour protéger nos droits</p>
                </div>

                <div className="sharing-card">
                  <h4>🔄 Transfert d'entreprise</h4>
                  <p>En cas de fusion, acquisition ou vente d'actifs (vous serez informé au préalable)</p>
                </div>
              </div>
            </section>

            {/* 4. Sécurité des données */}
            <section className="privacy-section">
              <h2>4. Sécurité de vos données</h2>
              
              <div className="subsection">
                <p>Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles pour protéger vos données :</p>
                <ul>
                  <li>🔒 <strong>Chiffrement SSL/TLS :</strong> Toutes les communications sont cryptées</li>
                  <li>🔐 <strong>Mots de passe sécurisés :</strong> Hashage et salage des mots de passe</li>
                  <li>🛡️ <strong>Pare-feu et protection :</strong> Systèmes de détection d'intrusion</li>
                  <li>👥 <strong>Accès limité :</strong> Seul le personnel autorisé peut accéder aux données</li>
                  <li>📊 <strong>Surveillance continue :</strong> Détection des activités suspectes</li>
                  <li>💾 <strong>Sauvegardes régulières :</strong> Protection contre la perte de données</li>
                </ul>
              </div>
            </section>

            {/* 5. Vos droits */}
            <section className="privacy-section">
              <h2>5. Vos droits sur vos données</h2>
              
              <div className="subsection">
                <p>Conformément au RGPD et aux lois marocaines sur la protection des données, vous disposez des droits suivants :</p>
                
                <div className="rights-grid">
                  <div className="right-card">
                    <div className="right-icon">👁️</div>
                    <h4>Droit d'accès</h4>
                    <p>Obtenir une copie de vos données personnelles</p>
                  </div>

                  <div className="right-card">
                    <div className="right-icon">✏️</div>
                    <h4>Droit de rectification</h4>
                    <p>Corriger vos données inexactes ou incomplètes</p>
                  </div>

                  <div className="right-card">
                    <div className="right-icon">🗑️</div>
                    <h4>Droit à l'effacement</h4>
                    <p>Supprimer vos données dans certaines conditions</p>
                  </div>

                  <div className="right-card">
                    <div className="right-icon">⏸️</div>
                    <h4>Droit de limitation</h4>
                    <p>Limiter le traitement de vos données</p>
                  </div>

                  <div className="right-card">
                    <div className="right-icon">📤</div>
                    <h4>Droit à la portabilité</h4>
                    <p>Recevoir vos données dans un format structuré</p>
                  </div>

                  <div className="right-card">
                    <div className="right-icon">🚫</div>
                    <h4>Droit d'opposition</h4>
                    <p>Vous opposer au traitement de vos données</p>
                  </div>
                </div>

                <div className="exercise-rights">
                  <p><strong>Pour exercer ces droits, contactez-nous à :</strong></p>
                  <a href="mailto:privacy@gatesofmusic.com" className="contact-email">
                    contact@gatesofmusic.com
                  </a>
                </div>
              </div>
            </section>

            {/* 6. Cookies */}
            <section className="privacy-section">
              <h2>6. Cookies et technologies similaires</h2>
              
              <div className="subsection">
                <p>Nous utilisons des cookies pour améliorer votre expérience. Vous pouvez gérer vos préférences de cookies à tout moment.</p>
                
                <div className="cookie-types">
                  <div className="cookie-type">
                    <h4>🍪 Cookies essentiels</h4>
                    <p>Nécessaires au fonctionnement du site (connexion, panier, sécurité)</p>
                  </div>

                  <div className="cookie-type">
                    <h4>📊 Cookies analytiques</h4>
                    <p>Nous aident à comprendre comment vous utilisez le site (Google Analytics)</p>
                  </div>

                  <div className="cookie-type">
                    <h4>🎯 Cookies marketing</h4>
                    <p>Utilisés pour la publicité ciblée (avec votre consentement)</p>
                  </div>

                  <div className="cookie-type">
                    <h4>⚙️ Cookies de préférences</h4>
                    <p>Mémorisent vos choix (langue, localisation, préférences d'affichage)</p>
                  </div>
                </div>
              </div>
            </section>

            {/* 7. Conservation des données */}
            <section className="privacy-section">
              <h2>7. Durée de conservation</h2>
              
              <div className="subsection">
                <p>Nous conservons vos données personnelles uniquement le temps nécessaire aux finalités pour lesquelles elles ont été collectées :</p>
                <ul>
                  <li>📝 <strong>Données de compte :</strong> Tant que votre compte est actif + 3 ans après suppression</li>
                  <li>💳 <strong>Données de transaction :</strong> 10 ans (obligations légales comptables)</li>
                  <li>📧 <strong>Communications marketing :</strong> 3 ans après le dernier contact</li>
                  <li>🍪 <strong>Cookies :</strong> Durée variable selon le type (13 mois maximum pour les cookies analytiques)</li>
                </ul>
              </div>
            </section>

            {/* 8. Mineurs */}
            <section className="privacy-section">
              <h2>8. Protection des mineurs</h2>
              
              <div className="subsection">
                <p>
                  Notre service est destiné aux personnes âgées de 18 ans et plus. Nous ne collectons pas 
                  sciemment d'informations personnelles auprès de mineurs. Si vous pensez qu'un mineur nous a 
                  fourni des données, veuillez nous contacter immédiatement.
                </p>
              </div>
            </section>

            {/* 9. Transferts internationaux */}
            <section className="privacy-section">
              <h2>9. Transferts internationaux de données</h2>
              
              <div className="subsection">
                <p>
                  Vos données peuvent être transférées et stockées sur des serveurs situés en dehors du Maroc. 
                  Nous nous assurons que ces transferts respectent les normes de protection des données et que 
                  des garanties appropriées sont en place (clauses contractuelles types, etc.).
                </p>
              </div>
            </section>

            {/* 10. Modifications */}
            <section className="privacy-section">
              <h2>10. Modifications de cette politique</h2>
              
              <div className="subsection">
                <p>
                  Nous pouvons mettre à jour cette politique de confidentialité de temps à autre. Nous vous 
                  informerons de tout changement important par e-mail ou via une notification sur la plateforme. 
                  La date de "dernière mise à jour" en haut de cette page indique la dernière révision.
                </p>
              </div>
            </section>

            {/* 11. Contact */}
            <section className="privacy-section">
              <h2>11. Nous contacter</h2>
              
              <div className="subsection">
                <p>Pour toute question concernant cette politique de confidentialité ou vos données personnelles :</p>
                
                <div className="contact-info">
                  <div className="contact-method">
                    <span className="contact-icon">📧</span>
                    <div>
                      <strong>E-mail :</strong>
                      <a href="mailto:privacy@gatesofmusic.com">contact@gatesofmusic.com</a>
                    </div>
                  </div>

                  <div className="contact-method">
                    <span className="contact-icon">📞</span>
                    <div>
                      <strong>Téléphone :</strong>
                      <a href="tel:+212123456789">+212624-381311</a>
                    </div>
                  </div>

                  <div className="contact-method">
                    <span className="contact-icon">📍</span>
                    <div>
                      <strong>Adresse :</strong>
                      <p>Gates of Music<br />Marrakech, Maroc</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer de la page */}
            <div className="privacy-footer">
              <p>
                En continuant à utiliser Gates of Music, vous acceptez cette politique de confidentialité. 
              </p>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;